import DropdownLinks from '@/components/DropdownLinks'
import Link from '@/components/Link'
import PlatformAlertBanner from '@/components/PlatformAlertBanner'
import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { PortalType, UserRole } from '@/generated/gql/graphql'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import useTenant from '@/hooks/useTenant'
import { resolveLogoVariant } from '@caterdesk/ui--feedr-logo'
import { ExpandMore, Menu as MenuIcon, ShoppingCart } from '@mui/icons-material'
import {
  AppBar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'

import Image from 'next/image'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { CANTEEN_NAVBAR_ID } from '../shared/constants'

const canteenNavbarUserDataFragment = graphql(`
  fragment CanteenNavbar_UserDataFragment on User {
    firstName
    lastName
    role
    account {
      isCCActive
      userInterfaceSettings {
        hideCCPrices
      }
    }
  }
`)

type Props = {
  userData: FragmentType<typeof canteenNavbarUserDataFragment>
  cartTotal: number
  cartItemQuantity: number
  onCartPreviewClick: () => void
  useNewCcUi?: boolean
}

const Navbar: React.FC<Props> = ({
  userData: userDataProp,
  cartTotal,
  cartItemQuantity,
  onCartPreviewClick,
  useNewCcUi = false,
}) => {
  const userData = useFragment(canteenNavbarUserDataFragment, userDataProp)
  const [anchorElMobileNav, setAnchorElMobileNav] = React.useState<null | HTMLElement>(null)
  const { t } = useTranslation()
  const tenant = useTenant()
  const theme = useTheme()
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'))
  const priceString = !userData.account?.userInterfaceSettings.hideCCPrices
    ? `${cartTotal.toFixed(2)}`
    : ''
  const sidebarNavEnabled = useFeatureFlag(SPLITS.DASHBOARD_SIDEBAR_NAV)
  const links = [
    { name: 'My Dashboard', url: '/dashboard' },

    ...(!sidebarNavEnabled && userData.role === UserRole.Manager
      ? [{ name: 'Admin Dashboard', url: `/canteen/ca` }]
      : []),
    { name: 'Try Catering?', url: '/office-catering/vendors' },
    {
      name: 'Log out',
      url: '/authentication/logout',
      color: theme.palette.error.main,
    },
  ]

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMobileNav(event.currentTarget)
  }

  const handleCloseNavMenu = () => {
    setAnchorElMobileNav(null)
  }

  return (
    <AppBar
      id={CANTEEN_NAVBAR_ID}
      position={useNewCcUi ? 'sticky' : 'static'}
      elevation={0}
      sx={{
        backgroundColor: 'inverted.main',
        boxShadow: '0 2px 10px 0 rgba(0, 0, 0, 0.05)',
      }}>
      <PlatformAlertBanner portalType={PortalType.CloudCanteen} />
      <Toolbar>
        <Stack direction="row" justifyContent="space-between" width="100%">
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
            <Link href="/">
              <Image
                src={resolveLogoVariant({
                  setToRainbowDuringPride: true,
                  logos: tenant.marketing.logos,
                })}
                alt={tenant.name}
                height={50}
                width={150}
                style={{ width: 'auto', height: '50px' }}
              />
            </Link>
            <Typography variant="caption">CANTEEN</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Chip
              data-cy="canteenCartIcon"
              clickable
              onClick={onCartPreviewClick}
              color="inverted"
              size="medium"
              label={`${cartItemQuantity} ${!isMobileScreen ? `${t('cc:items', { count: cartItemQuantity })}${priceString ? ', ' : ''}${priceString}` : ''}`}
              style={{ border: `0.5px solid ${theme.palette.primary.light}` }}
              icon={<ShoppingCart fontSize="small" />}
            />
            <Stack direction="row" alignItems="center" display={{ xs: 'none', md: 'flex' }}>
              <DropdownLinks
                id="profile"
                dataCy="account-link-user-info"
                label={`${userData.firstName} ${userData.lastName}`}
                links={links}
              />
              <ExpandMore htmlColor={theme.palette.text.secondary} />
            </Stack>
            <Stack direction="row" alignItems="center" display={{ xs: 'flex', md: 'none' }}>
              <IconButton onClick={handleOpenNavMenu} size="large" edge="start" aria-label="menu">
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElMobileNav}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                open={Boolean(anchorElMobileNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: 'block', md: 'none' },
                }}>
                {links.map((link) => (
                  <MenuItem key={link.name} onClick={handleCloseNavMenu}>
                    <Link
                      key={link.name}
                      passHref
                      href={link.url}
                      style={{ textDecoration: 'none' }}>
                      <Typography textAlign="center">{link.name}</Typography>
                    </Link>
                  </MenuItem>
                ))}
              </Menu>
            </Stack>
          </Stack>
        </Stack>
      </Toolbar>
    </AppBar>
  )
}

export default Navbar
